import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Activity, CheckCircle2, AlertTriangle, XCircle, Clock } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import MetricCard from "@/components/dashboard/MetricCard";

const nz = new Intl.NumberFormat("en-NZ");

const STATUS = {
    ok:      { Icon: CheckCircle2,  color: "#22C55E", label: "Operational", bg: "bg-[#22C55E]/5",  border: "border-[#22C55E]/30" },
    warning: { Icon: AlertTriangle, color: "#f59e0b", label: "Degraded",    bg: "bg-[#f59e0b]/10", border: "border-[#f59e0b]/40" },
    error:   { Icon: XCircle,       color: "#ef4444", label: "Outage",      bg: "bg-[#ef4444]/10", border: "border-[#ef4444]/40" },
};
const SEVERITY = { high: "#ef4444", medium: "#f59e0b", low: "#22C55E" };

export default function HealthDashboard() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        let cancel = false;
        (async () => {
            try {
                const { data } = await api.get("/metrics/health");
                if (!cancel) setData(data);
            } catch (e) {
                if (!cancel) setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
            }
        })();
        return () => { cancel = true; };
    }, []);

    if (err) {
        return (
            <div className="p-8">
                <div className="hg-card p-5 border-[#ef4444]/40">
                    <div className="hg-eyebrow text-[#fecaca]">// Error</div>
                    <div className="font-mono-data mt-1">{err}</div>
                </div>
            </div>
        );
    }
    if (!data) return <Skeleton />;

    const s = data.sessions;
    const overall = STATUS[data.overall_status];

    return (
        <div className="min-h-full px-4 md:px-8 py-6 md:py-8" data-testid="health-dashboard">
            <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 pb-6 border-b border-white/5">
                <div>
                    <div className="hg-eyebrow mb-2">// Section 05 · App Health</div>
                    <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#FAFAFA] leading-none">App Health</h1>
                    <p className="mt-2 text-sm text-[#A1A1AA]">Sessions, screen traffic, integration status and live error flags.</p>
                </div>
                <div className={`flex items-center gap-2 px-3 py-2 rounded-sm border ${overall.bg} ${overall.border}`} data-testid="overall-status">
                    <overall.Icon size={14} style={{ color: overall.color }} />
                    <span className="font-mono-data text-[11px] uppercase tracking-widest" style={{ color: overall.color }}>
                        {data.overall_status === "ok" ? "All systems operational" : data.overall_status === "warning" ? "Partial degradation" : "Integration outage"}
                    </span>
                </div>
            </header>

            {/* Sessions */}
            <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mt-6">
                <MetricCard label="// Sessions · today" value={nz.format(s.today)} accent tag="LIVE" testid="metric-sessions-today">
                    <MiniBars data={s.hourly_today} />
                </MetricCard>
                <MetricCard label="// Sessions · 7 days" value={nz.format(s.week)} deltaPct={s.week_delta_pct} deltaLabel="prev week" testid="metric-sessions-week" />
                <MetricCard label="// Sessions · 30 days" value={nz.format(s.month)} testid="metric-sessions-month">
                    <MiniBars data={s.daily_30d} />
                </MetricCard>
                <div className="hg-card p-5 flex flex-col gap-3" data-testid="metric-session-quality">
                    <div className="hg-eyebrow">// Session quality</div>
                    <div className="flex items-baseline gap-2">
                        <span className="font-mono-data text-3xl text-[#FAFAFA]">{Math.floor(s.avg_session_s / 60)}m {s.avg_session_s % 60}s</span>
                        <span className="font-mono-data text-xs text-[#71717a]">avg</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="font-mono-data text-xs text-[#22C55E]">{s.crash_free_pct}%</span>
                        <span className="hg-eyebrow text-[10px]">crash-free sessions</span>
                    </div>
                </div>
            </section>

            {/* Screens + Integrations */}
            <section className="grid grid-cols-1 lg:grid-cols-6 gap-4 mt-4">
                <div className="hg-card p-5 lg:col-span-3" data-testid="top-screens">
                    <div className="flex items-center justify-between pb-3 border-b border-white/5">
                        <div>
                            <div className="hg-eyebrow">// Most visited screens</div>
                            <div className="font-heading text-xl text-[#FAFAFA] mt-1">Last 30 days</div>
                        </div>
                        <Activity size={14} className="text-[#22C55E]" />
                    </div>
                    <ul className="mt-3 flex flex-col gap-1">
                        {data.top_screens.map((sc) => (
                            <li key={sc.rank} className="group flex items-center gap-3 py-2 border-l-2 border-transparent hover:border-[#22C55E] hover:bg-white/[0.02] px-2 transition-colors" data-testid={`screen-${sc.rank}`}>
                                <span className="font-mono-data text-[10px] text-[#52525b] w-5">{String(sc.rank).padStart(2, "0")}</span>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-baseline justify-between gap-2">
                                        <span className="font-mono-data text-xs text-[#FAFAFA]">{sc.screen}</span>
                                        <span className="font-mono-data text-[11px] text-[#A1A1AA]">{nz.format(sc.views)} <span className="text-[#22C55E]">· {sc.pct}%</span></span>
                                    </div>
                                    <div className="h-[2px] mt-1 bg-white/5 rounded-full overflow-hidden">
                                        <div className="h-full bg-[#22C55E]" style={{ width: `${(sc.views / data.top_screens[0].views) * 100}%`, boxShadow: "0 0 6px rgba(34,197,94,0.5)" }} />
                                    </div>
                                </div>
                                <span className="flex items-center gap-1 font-mono-data text-[10px] text-[#71717a] w-14 justify-end"><Clock size={10} />{sc.avg_time_s}s</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="hg-card p-5 lg:col-span-3" data-testid="integrations">
                    <div className="flex items-center justify-between pb-3 border-b border-white/5">
                        <div>
                            <div className="hg-eyebrow">// Integrations</div>
                            <div className="font-heading text-xl text-[#FAFAFA] mt-1">{data.integrations.length} services monitored</div>
                        </div>
                        <div className="hg-eyebrow text-[10px]">{data.integrations.filter((i) => i.status !== "ok").length} need attention</div>
                    </div>
                    <div className="mt-3 space-y-2">
                        {data.integrations.map((it) => {
                            const st = STATUS[it.status];
                            return (
                                <div key={it.name} className={`${st.bg} border ${st.border} rounded-sm px-3 py-2.5`} data-testid={`integration-${it.name.toLowerCase().replace(/\s/g, "-")}`}>
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <st.Icon size={13} style={{ color: st.color }} />
                                            <span className="font-mono-data text-sm text-[#FAFAFA]">{it.name}</span>
                                            <span className="hg-eyebrow text-[9px] truncate">{it.kind}</span>
                                        </div>
                                        <span className="font-mono-data text-[10px] uppercase tracking-widest shrink-0" style={{ color: st.color }}>{st.label}</span>
                                    </div>
                                    <div className="mt-1 font-mono-data text-[11px] text-[#A1A1AA]">{it.message}</div>
                                    <div className="mt-1.5 flex gap-4 hg-eyebrow text-[9px]">
                                        <span>Latency <span className="text-[#FAFAFA]">{it.latency_ms ? `${it.latency_ms}ms` : "—"}</span></span>
                                        <span>Uptime 30d <span className="text-[#FAFAFA]">{it.uptime_pct}%</span></span>
                                        <span>Checked <span className="text-[#FAFAFA]">{Math.max(1, Math.round((Date.now() - new Date(it.last_check)) / 1000))}s ago</span></span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Error flags */}
            <section className="hg-card p-5 mt-4" data-testid="error-flags">
                <div className="flex items-center justify-between pb-3 border-b border-white/5">
                    <div className="flex items-center gap-2">
                        <AlertTriangle size={14} className="text-[#f59e0b]" />
                        <span className="hg-eyebrow">// Active error flags</span>
                    </div>
                    <div className="hg-eyebrow text-[10px] text-[#f59e0b]">{data.error_flags.length} open</div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                    {data.error_flags.map((f) => (
                        <div key={f.id} className="border border-white/[0.06] rounded-sm px-3 py-3 hover:border-white/15 transition-colors" data-testid={`error-${f.id.toLowerCase()}`}>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: SEVERITY[f.severity], boxShadow: `0 0 6px ${SEVERITY[f.severity]}` }} />
                                    <span className="hg-eyebrow text-[10px]">{f.id}</span>
                                </div>
                                <span className="font-mono-data text-[10px] uppercase tracking-widest" style={{ color: SEVERITY[f.severity] }}>{f.severity}</span>
                            </div>
                            <div className="mt-1.5 font-mono-data text-sm text-[#FAFAFA]">{f.title}</div>
                            <div className="mt-1 font-mono-data text-[11px] text-[#A1A1AA]">{f.detail}</div>
                            <div className="mt-2 flex gap-4 hg-eyebrow text-[9px]">
                                <span><span className="text-[#FAFAFA]">{nz.format(f.count_24h)}</span> events · 24h</span>
                                <span>first seen <span className="text-[#FAFAFA]">{f.first_seen_h}h</span> ago</span>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}

const MiniBars = ({ data }) => (
    <div className="h-[40px] -mx-1">
        <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 0, right: 0, left: 0, bottom: 0 }} barCategoryGap={1}>
                <XAxis dataKey="date" hide />
                <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} content={({ active, payload }) =>
                    active && payload?.length ? (
                        <div className="hg-glass px-2 py-1 rounded-sm border border-white/10 font-mono-data text-[10px] text-[#FAFAFA]">
                            {nz.format(Math.round(payload[0].value))}
                        </div>
                    ) : null
                } />
                <Bar dataKey="value" radius={[1, 1, 0, 0]}>
                    {data.map((_, i) => <Cell key={i} fill="#22C55E" fillOpacity={i === data.length - 1 ? 1 : 0.45} />)}
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    </div>
);

const Skeleton = () => (
    <div className="p-8 space-y-4">
        <div className="h-12 w-80 bg-white/5 rounded-sm animate-pulse" />
        <div className="grid grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => <div key={i} className="hg-card h-[140px] animate-pulse" />)}
        </div>
        <div className="hg-card h-[420px] animate-pulse" />
    </div>
);
